import { useCallback, useMemo } from 'react'

import {
  AllConditions,
  Almanac,
  ConditionProperties,
  Engine,
  Event,
  NestedCondition,
  RuleResult,
} from 'json-rules-engine'
import { cloneDeep, groupBy } from 'lodash-es'

import {
  INITIAL_WELLBEING_RESPONSE,
  WellbeingResult,
  WELLNESS_CHECK_IN_FACET_GROUP_TO_DOMAIN,
  WellnessCheckInDomain,
  WellnessCheckInFacetGroup,
  WellnessCheckInValency,
} from '../constants'
import { getWellnessCheckInMetadata } from '../wellnessCheckInMetadata'

const RULE_TYPES = {
  FACET_COMPLETE: 'facetComplete', // rule to check if facet group is either 100% answered, or >50% answered for domains with 1 facet group
  ANSWERED: 'answered',
} as const

const OPERATORS = {
  EQUAL: 'equal',
  GREATER_THAN_INCLUSIVE: 'greaterThanInclusive',
} as const

const FACT_TYPES = {
  RUN_WELLBEING_RULES: 'runWellbeingRules',
  IS_COMPLETE: 'isComplete',
} as const

/**
 * A hook that is used for the Wellness Check In Form.
 * Defines the rules and uses them to initialize the rules engine.
 * @returns a callback function that returns an object of the user's form values and the wellbeingResponse constructed
 * from those values. The wellbeing response includes the score of each domain and the wellbeing result for each facet.
 */
export const useWellnessCheckInRulesEngine = () => {
  const wellbeingResponse = cloneDeep(INITIAL_WELLBEING_RESPONSE)
  const VALENCY_INVERSION = 7
  const { schema } = getWellnessCheckInMetadata()

  const getDomainHasSingleFacetGroup = (domain: WellnessCheckInDomain) => {
    return (
      Object.values(WellnessCheckInFacetGroup).filter(
        (facetGroup) => WELLNESS_CHECK_IN_FACET_GROUP_TO_DOMAIN[facetGroup] === domain,
      ).length === 1
    )
  }

  const domainSingleFacetGroupCache = useMemo(() => {
    const cache: Record<WellnessCheckInDomain, boolean> = {} as Record<WellnessCheckInDomain, boolean>
    Object.values(WellnessCheckInDomain).forEach((domain) => {
      cache[domain] = getDomainHasSingleFacetGroup(domain)
    })
    return cache
  }, [])

  /** arbitrary high number for set of rules that should run first */
  const HIGH_PRIORITY = 100

  /**
   * create 1 rule per facet group which checks if the user answered all questions in that facet group.
   * on success (user answered all questions in facet group):
   *   - set new fact facetGroup.isComplete with value true
   * on failure (user skipped at least one question in facet group):
   *   - if domain has only 1 facet group, check if >50% answered
   *     - if yes, set new fact facetGroup.isComplete with value true
   *   - else set new fact facetGroup.isComplete with value false
   * set priority to HIGH_PRIORITY to ensure this set of rules runs before the low / high wellbeing rules.
   */
  const createAnsweredEntireFacetGroupRules = useCallback(
    (answeredEntireFacetGroupConditionsMapping: Record<string, NestedCondition[]>) => {
      return Object.keys(answeredEntireFacetGroupConditionsMapping).map((facetGroupName) => {
        const facetGroup = facetGroupName as WellnessCheckInFacetGroup
        const conditions = answeredEntireFacetGroupConditionsMapping[facetGroup]
        const domain = WELLNESS_CHECK_IN_FACET_GROUP_TO_DOMAIN[facetGroup]
        const domainHasSingleFacetGroup = domainSingleFacetGroupCache[domain]

        return {
          conditions: { all: conditions },
          event: { type: RULE_TYPES.FACET_COMPLETE, params: { facetGroup } },
          priority: HIGH_PRIORITY,
          onSuccess: (event: Event, almanac: Almanac) => {
            almanac.addFact(`${facetGroup}.${FACT_TYPES.IS_COMPLETE}`, true)
          },
          onFailure: (event: Event, almanac: Almanac, ruleResult: RuleResult) => {
            if (domainHasSingleFacetGroup) {
              // For single-facet domains, check if >50% answered
              const answeredQuestions = (ruleResult.conditions as AllConditions).all.filter(
                (condition) => (condition as NestedCondition & { result: boolean }).result,
              )
              const isAboveThreshold = answeredQuestions.length / conditions.length > 0.5
              almanac.addFact(`${facetGroup}.${FACT_TYPES.IS_COMPLETE}`, isAboveThreshold)
            } else {
              almanac.addFact(`${facetGroup}.${FACT_TYPES.IS_COMPLETE}`, false)
            }
          },
        }
      })
    },
    [domainSingleFacetGroupCache],
  )

  const calculateDomainScore = useCallback(
    (domain: WellnessCheckInDomain, conditions: NestedCondition[]) => {
      const domainTotalScore = conditions
        .map((condition) => {
          const nestedSchema = (condition as ConditionProperties).path!.split('.')
          const questionName = nestedSchema[nestedSchema.length - 1]
          const questionValue = (condition as ConditionProperties & { factResult: number }).factResult
          const isLowValencyQuestion = schema.properties![questionName].valency === WellnessCheckInValency.Low
          /**
           * Invert the score for low valency questions (e.g. I felt down or depressed)
           * because higher value means lower wellbeing for those questions.
           * 6 becomes 1, 5 becomes 2, 4 becomes 3, etc.
           * This is necessary to standardize the values before calculating the
           * average score for each domain since higher value indicates higher wellbeing.
           */
          return isLowValencyQuestion ? VALENCY_INVERSION - questionValue : questionValue
        })
        .reduce((acc, prev) => acc + prev)

      wellbeingResponse[domain as WellnessCheckInDomain].score =
        Math.round((domainTotalScore / conditions.length) * 100) / 100
    },
    [schema.properties, wellbeingResponse],
  )

  const skipDomain = (conditions: NestedCondition[]) => {
    const numTotalQuestions = conditions.length
    const answeredQuestions = conditions.filter(
      (condition) => (condition as NestedCondition & { result: boolean }).result,
    )
    /**
     * Skip the domain if less than half of the questions in that domain have been answered
     */
    return answeredQuestions.length / numTotalQuestions < 0.5
  }

  /**
   * create 1 rule per domain which checks if the user skipped any questions.
   * on success (user answered all questions in domain):
   *   - call calculateDomainScore to update the wellbeingResponse with domain score
   *   - set new fact domain.runWellbeingRules with value true
   *     so the wellbeing rules run for that domain
   * on failure (user skipped at least one question in domain):
   *   if user answered < 50% of questions in that domain
   *     - set new fact domain.runWellbeingRules with value false
   *       so the wellbeing rules do not run for that domain
   *   else the user has answered >= 50% questions in that domain
   *     - call calculateDomainScore to update the wellbeingResponse with domain score
   *     - set new fact domain.runWellbeingRules with value true
   *       so the wellbeing rules run for that domain
   * set priority to HIGH_PRIORITY to ensure this set of rules runs before the low / high wellbeing rules.
   */
  const createAnsweredEntireDomainRules = useCallback(
    (answeredEntireDomainConditionsMapping: Record<string, NestedCondition[]>) => {
      return Object.keys(answeredEntireDomainConditionsMapping).map((domainName) => {
        const domain = domainName as WellnessCheckInDomain
        const conditions = answeredEntireDomainConditionsMapping[domain] as NestedCondition[]
        return {
          conditions: { all: conditions },
          event: { type: RULE_TYPES.ANSWERED, params: { domain: domain } },
          priority: HIGH_PRIORITY,
          onSuccess: function (event: Event, almanac: Almanac, ruleResult: RuleResult) {
            calculateDomainScore(domain, (ruleResult.conditions as AllConditions).all)
            almanac.addFact(`${domain}.${FACT_TYPES.RUN_WELLBEING_RULES}`, true)
          },
          onFailure: function (event: Event, almanac: Almanac, ruleResult: RuleResult) {
            if (skipDomain((ruleResult.conditions as AllConditions).all)) {
              almanac.addFact(`${domain}.${FACT_TYPES.RUN_WELLBEING_RULES}`, false)
            } else {
              const answeredQuestions = (ruleResult.conditions as AllConditions).all.filter(
                (condition) => (condition as NestedCondition & { result: boolean }).result,
              )
              calculateDomainScore(domain, answeredQuestions)
              almanac.addFact(`${domain}.${FACT_TYPES.RUN_WELLBEING_RULES}`, true)
            }
          },
        }
      })
    },
    [calculateDomainScore],
  )

  const getFacetGroupPriority = (facetGroup: WellnessCheckInFacetGroup) => {
    return Object.keys(WellnessCheckInFacetGroup).length - Object.values(WellnessCheckInFacetGroup).indexOf(facetGroup)
  }

  /**
   * create 1 low wellbeing rule per facet group.
   * event fires if domain.runWellbeingRules is true AND
   * ANY of the low wellbeing conditions in that facet group is true.
   * priority is set so that it's run in the order of WellnessCheckInFacetGroup enum
   * (e.g. the first facet group Mood has priority set to 13, the last facet group
   * Nature has priority 1; higher number = higher priority).
   * priority value range is 1-13 which ensures this set of rules runs after
   * answeredEntireFacetGroupRules and answeredEntireDomainRules.
   */
  const createLowWellbeingRules = useCallback((lowWellbeingConditionsMapping: Record<string, NestedCondition[]>) => {
    return Object.keys(lowWellbeingConditionsMapping).map((facetGroupName) => {
      const facetGroup = facetGroupName as WellnessCheckInFacetGroup
      const domain = WELLNESS_CHECK_IN_FACET_GROUP_TO_DOMAIN[facetGroup]
      const conditions = lowWellbeingConditionsMapping[facetGroup] as NestedCondition[]
      const priority = getFacetGroupPriority(facetGroup)

      return {
        conditions: {
          all: [
            {
              fact: `${domain}.${FACT_TYPES.RUN_WELLBEING_RULES}`,
              operator: OPERATORS.EQUAL,
              value: true,
            },
            {
              fact: `${facetGroup}.${FACT_TYPES.IS_COMPLETE}`,
              operator: OPERATORS.EQUAL,
              value: true,
            },
            {
              any: conditions,
            },
          ],
        },
        priority,
        event: { type: WellbeingResult.Low, params: { facetGroup: facetGroup } },
      }
    })
  }, [])

  /**
   * create 1 high wellbeing rule per facet group.
   * event fires if domain.runWellbeingRules is true AND
   * ALL of the high wellbeing conditions in that facet group are true.
   * priority is set so that it's run in the order of WellnessCheckInFacetGroup enum
   * (e.g. the first facet group Mood has priority set to 13, the last facet group
   * Nature has priority 1; higher number = higher priority).
   * priority value range is 1-13 which ensures this set of rules runs after
   * answeredEntireFacetGroupRules and answeredEntireDomainRules.
   */
  const createHighWellbeingRules = useCallback((highWellbeingConditionsMapping: Record<string, NestedCondition[]>) => {
    return Object.keys(highWellbeingConditionsMapping).map((facetGroupName) => {
      const facetGroup = facetGroupName as WellnessCheckInFacetGroup
      const domain = WELLNESS_CHECK_IN_FACET_GROUP_TO_DOMAIN[facetGroup]
      const conditions = highWellbeingConditionsMapping[facetGroup] as NestedCondition[]
      const priority = getFacetGroupPriority(facetGroup)

      /**
       * create new set of conditions from original high wellbeing conditions
       * to handle the scenario where user doesn't answer ALL the questions,
       * but they answer >50% and all of the answered questions indicate high wellbeing.
       */
      const conditionsOrSkipped = conditions.map((condition) => {
        let newCondition = { ...condition }
        if ((condition as AllConditions).all) {
          newCondition = (condition as AllConditions).all[0] as ConditionProperties
        }
        return {
          any: [
            condition,
            {
              not: {
                fact: domain,
                operator: OPERATORS.GREATER_THAN_INCLUSIVE,
                value: 1,
                path: (newCondition as ConditionProperties).path,
              },
            },
          ],
        }
      })
      return {
        conditions: {
          all: [
            {
              fact: `${domain}.${FACT_TYPES.RUN_WELLBEING_RULES}`,
              operator: OPERATORS.EQUAL,
              value: true,
            },
            {
              fact: `${facetGroup}.${FACT_TYPES.IS_COMPLETE}`,
              operator: OPERATORS.EQUAL,
              value: true,
            },
            ...conditionsOrSkipped,
          ],
        },
        priority,
        event: { type: WellbeingResult.High, params: { facetGroup: facetGroup } },
      }
    })
  }, [])

  const getRules = useCallback(() => {
    /**
     * iterate through schema and create three arrays of conditions for:
     * 1. user answered the question (value >= 1)
     * 2. user's answer indicates low wellbeing
     * 3. user's answer indicates high wellbeing
     */
    const [answeredEntireDomainConditionsArray, lowWellbeingConditionsArray, highWellbeingConditionsArray] =
      Object.values(schema.properties!).reduce(
        (
          [answered, low, high]: [NestedCondition[], NestedCondition[], NestedCondition[]],
          { engineRuleConditions },
        ) => {
          if (engineRuleConditions) {
            answered.push(engineRuleConditions?.answeredCondition)
            low.push(engineRuleConditions?.lowWellbeingCondition)
            high.push(engineRuleConditions?.highWellbeingCondition)
          }

          return [answered, low, high]
        },
        [[], [], []],
      ) as [NestedCondition[], NestedCondition[], NestedCondition[]]

    const answeredEntireFacetGroupConditionsMapping = groupBy(
      answeredEntireDomainConditionsArray,
      ({ path }: { path: string }) => path!.split('.')[1],
    )

    /**
     * using answeredEntireDomainConditionsArray created above, create a mapping
     * where key is domain and value is array of conditions.
     * the mapping will be used to create a rule for each domain
     * to determine whether user skipped more than 50% of questions in that domain
     */
    const answeredEntireDomainConditionsMapping = groupBy(
      answeredEntireDomainConditionsArray,
      ({ fact }: { fact: string }) => fact,
    )

    /**
     * using lowWellbeingConditionsArray created above, create a mapping
     * where key is facet group and value is array of conditions.
     * the mapping will be used to create a rule for each facet group
     * to determine whether the user's answers indicate low wellbeing
     * path.split('.')[1] gets the facet group name
     */
    const lowWellbeingConditionsMapping = groupBy(
      lowWellbeingConditionsArray,
      ({ path, all }: { path?: string; all?: NestedCondition[] }) => {
        if (all) {
          const condition = all[0] as ConditionProperties
          return condition.path!.split('.')[1]
        }
        return path!.split('.')[1]
      },
    )

    /** same as above but for high wellbeing */
    const highWellbeingConditionsMapping = groupBy(
      highWellbeingConditionsArray as Array<ConditionProperties & AllConditions>,
      ({ path, all }: { path?: string; all?: NestedCondition[] }) => {
        if (all) {
          const condition = all[0] as ConditionProperties
          return condition.path!.split('.')[1]
        }
        return path!.split('.')[1]
      },
    )

    const rules = [
      ...createAnsweredEntireFacetGroupRules(
        answeredEntireFacetGroupConditionsMapping as Record<string, NestedCondition[]>,
      ),
      ...createAnsweredEntireDomainRules(answeredEntireDomainConditionsMapping as Record<string, NestedCondition[]>),
      ...createLowWellbeingRules(lowWellbeingConditionsMapping as Record<string, NestedCondition[]>),
      ...createHighWellbeingRules(highWellbeingConditionsMapping),
    ]
    return rules
  }, [
    schema.properties,
    createAnsweredEntireFacetGroupRules,
    createAnsweredEntireDomainRules,
    createLowWellbeingRules,
    createHighWellbeingRules,
  ])

  const engine = useMemo(() => new Engine(getRules(), { allowUndefinedFacts: true }), [getRules])

  const processRuleEvents = useCallback(
    (events: Event[]) => {
      /** in the case where the user has already submitted the form, press back to change their input
       * and resubmit the form, the wellbeing response will already be populated with wellbeing result arrays for each domain
       * (for example, the MOOD AND EMOTIONS domain will already have wellbeing results for facet groups MOOD and EMOTIONS).
       * clear the array for each domain so that the new wellbeing results are captured and override the old ones.
       */
      Object.values(WellnessCheckInDomain).forEach((domain: string) => {
        wellbeingResponse[domain as WellnessCheckInDomain].response = []
      })

      events.forEach((event: Event) => {
        const { type, params } = event
        /**
         * construct wellbeing response from events emitted from rules engine.
         * ignore events of type FACET_COMPLETE because we're only interested
         * in results of the low and high wellbeing events.
         */
        if (params?.facetGroup && type !== RULE_TYPES.FACET_COMPLETE) {
          const facetGroup = params.facetGroup as WellnessCheckInFacetGroup
          const domain = WELLNESS_CHECK_IN_FACET_GROUP_TO_DOMAIN[facetGroup]
          wellbeingResponse[domain].response.push({
            facetGroup,
            result: type as WellbeingResult,
          })
        }
      })
    },
    [wellbeingResponse],
  )

  const runRulesEngine = useCallback(
    async ({ values }: { values: object }) => {
      const { events } = await engine.run(values)
      processRuleEvents(events)
      return { ...values, wellbeingResponse }
    },
    [engine, wellbeingResponse, processRuleEvents],
  )

  return { runRulesEngine }
}
